import { PrimaryButton, PrimaryButtonProps, useModal } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'
import { useNavigate } from 'react-router'

import { getCurrentQuote } from '../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../hooks/auth'
import { usePromotion } from '../../../../hooks/covers'
import { useErrorHandlingAsyncCallback } from '../../../../hooks/errors'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { usePrincipal } from '../../../authentication'
import { IdCheckFailedModal } from '../../../organisms/proposal/IdCheckFailedModal'
import { usePaymentSchedule } from '../../paymentScheduleContext'
import { useStartCheckoutPath } from '../checkout'
import { ProposalPath } from '../ProposalPath'
import { useProposal } from '../ProposalProvider'

export interface ProposalContinueButtonProps extends PrimaryButtonProps {
  tKey: TKeyBuilder
  disabled?: boolean
}

export const ProposalContinueButton: FunctionComponent<ProposalContinueButtonProps> = ({
  tKey,
  disabled = false,
  ...props
}) => {
  const { t } = useWriskTranslation()
  const { apiClient } = usePrincipal()
  const modal = useModal()

  const navigate = useNavigate()

  const { proposal, setProposal } = useProposal()

  const { isPaymentScheduleDirty, getScheduleChanges } = usePaymentSchedule()

  const checkoutPath = useStartCheckoutPath()

  const quote = getCurrentQuote(proposal)

  const promotion = usePromotion(quote)

  const { execute: updateProposal } = useApiErrorHandlingAsyncCallback(async () => {
    if (isPaymentScheduleDirty) {
      setProposal(
        await apiClient.updateProposal(proposal.proposalCode, {
          applyRating: true,
          changes: getScheduleChanges(),
        }),
      )
    }
  })

  const onButtonClick = useErrorHandlingAsyncCallback(async () => {
    await updateProposal()
    const idCheckResponse = await apiClient.idCheck(proposal.proposalCode)

    idCheckResponse.isClear
      ? navigate([ProposalPath.CHECKOUT, checkoutPath].join('/'))
      : modal.show({
          content: <IdCheckFailedModal />,
        })
  })

  const buttonKey = promotion ? 'actions.active-promo' : 'actions.continue'

  return (
    <PrimaryButton
      data-testid='proposal-continue'
      onClick={onButtonClick.execute}
      disabled={disabled}
      loading={onButtonClick.loading}
      {...props}
    >
      {t(tKey(buttonKey))}
    </PrimaryButton>
  )
}
